import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { AppShell } from '@/components/layout/AppShell';
import { TerminalShell } from '@/components/layout/TerminalShell';
import { Login } from '@/features/auth/Login';
import { Forbidden, NotFound } from '@/features/auth/Forbidden';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { ProductsPage } from '@/features/products/ProductsPage';
import { FamiliesPage } from '@/features/products/FamiliesPage';
import { TreasuryPage } from '@/features/treasury/TreasuryPage';
import { CompanyPage } from '@/features/company/CompanyPage';
import { StockPage } from '@/features/stock/StockPage';
import { SuppliersPage } from '@/features/suppliers/SuppliersPage';
import { InvoicesPage } from '@/features/purchasing/InvoicesPage';
import { PurchaseOrdersPage } from '@/features/purchasing/PurchaseOrdersPage';
import { TariffPage } from '@/features/purchasing/TariffPage';
import { CurrenciesPage } from '@/features/purchasing/CurrenciesPage';
import { ModelsPage } from '@/features/models/ModelsPage';
import { ModelDetailPage } from '@/features/models/ModelDetailPage';
import { OrdersPage } from '@/features/orders/OrdersPage';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { OrderDetailPage } from '@/features/orders/OrderDetailPage';
import { KitsPage } from '@/features/kits/KitsPage';
import { KitDetailPage } from '@/features/kits/KitDetailPage';
import { WarehousePage } from '@/features/warehouse/WarehousePage';
import { PackagingPage } from '@/features/packaging/PackagingPage';
import { LogisticsPage } from '@/features/logistics/LogisticsPage';
import { PlanningPage } from '@/features/planning/PlanningPage';
import { AdminPage } from '@/features/admin/AdminPage';
import { AccountPage } from '@/features/account/AccountPage';
import { PERMISSIONS, TERMINAL_HOME, terminalLayout, type TerminalKind } from '@erp/shared';
import type { ReactNode } from 'react';

function Guard({ permission, anyOf, children }: { permission?: string; anyOf?: string[]; children: ReactNode }) {
  const { can } = useAuth();
  const allowed = permission ? can(permission) : (anyOf ?? []).some((item) => can(item));
  return allowed ? <>{children}</> : <Forbidden />;
}

/**
 * Elige el armazón por el **tipo de terminal**, no por la persona: en un puesto de fábrica
 * (almacén, embalaje, logística) no se pinta la cabecera de oficina con menús desplegables.
 * Las rutas son las mismas en los dos armazones.
 */
function ShellFor() {
  const { terminal } = useAuth();
  return terminalLayout(terminal?.kind) === 'station' ? <TerminalShell /> : <AppShell />;
}

function TerminalLanding() {
  const { terminal } = useAuth();
  const target = terminal ? TERMINAL_HOME[terminal.kind as TerminalKind] : '/';
  if (!target || target === '/') return <DashboardPage />;
  return <Navigate to={target} replace />;
}

function Splash() {
  return (
    <div className="flex h-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="flex size-12 animate-pulse-soft items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-teal-400 text-xl font-bold text-white shadow-lg shadow-blue-500/20">E</div>
        <p className="text-xs text-muted-foreground">Cargando Eurobath ERP…</p>
      </div>
    </div>
  );
}

export default function App() {
  const { session, loading } = useAuth();
  if (loading) return <Splash />;
  if (!session) return <Login />;

  return (
    <Routes>
      <Route element={<ShellFor />}>
        <Route index element={<Guard permission={PERMISSIONS.DASHBOARD_READ}><TerminalLanding /></Guard>} />
        <Route path="products" element={<Guard permission={PERMISSIONS.PRODUCTS_READ}><ProductsPage /></Guard>} />
        <Route path="families" element={<Guard permission={PERMISSIONS.PRODUCTS_READ}><FamiliesPage /></Guard>} />
        <Route path="treasury" element={<Guard permission={PERMISSIONS.FINANCE_READ}><TreasuryPage /></Guard>} />
        <Route path="company" element={<Guard permission={PERMISSIONS.FINANCE_READ}><CompanyPage /></Guard>} />
        <Route path="stock" element={<Guard permission={PERMISSIONS.STOCK_READ}><StockPage /></Guard>} />
        <Route path="suppliers" element={<Guard permission={PERMISSIONS.SUPPLIERS_READ}><SuppliersPage /></Guard>} />
        <Route path="invoices" element={<Guard permission={PERMISSIONS.PURCHASES_READ}><InvoicesPage /></Guard>} />
        <Route path="purchase-orders" element={<Guard permission={PERMISSIONS.PURCHASES_READ}><PurchaseOrdersPage /></Guard>} />
        <Route path="tariff" element={<Guard permission={PERMISSIONS.PURCHASES_READ}><TariffPage /></Guard>} />
        <Route path="currencies" element={<Guard permission={PERMISSIONS.FINANCE_READ}><CurrenciesPage /></Guard>} />
        <Route path="models" element={<Guard permission={PERMISSIONS.MIRRORS_READ}><ModelsPage /></Guard>} />
        <Route path="models/:id" element={<Guard permission={PERMISSIONS.MIRRORS_READ}><ModelDetailPage /></Guard>} />
        <Route path="customers" element={<Guard permission={PERMISSIONS.ORDERS_READ}><CustomersPage /></Guard>} />
        <Route path="orders" element={<Guard permission={PERMISSIONS.ORDERS_READ}><OrdersPage /></Guard>} />
        <Route path="orders/:id" element={<Guard permission={PERMISSIONS.ORDERS_READ}><OrderDetailPage /></Guard>} />
        <Route path="kits" element={<Guard permission={PERMISSIONS.KITS_READ}><KitsPage /></Guard>} />
        <Route path="kits/:id" element={<Guard permission={PERMISSIONS.KITS_READ}><KitDetailPage /></Guard>} />
        <Route path="warehouse" element={<Guard permission={PERMISSIONS.KITS_READ}><WarehousePage /></Guard>} />
        <Route path="packaging" element={<Guard permission={PERMISSIONS.PACKAGING_READ}><PackagingPage /></Guard>} />
        <Route path="logistics" element={<Guard permission={PERMISSIONS.LOGISTICS_READ}><LogisticsPage /></Guard>} />
        <Route path="planning" element={<Guard permission={PERMISSIONS.PLANNING_READ}><PlanningPage /></Guard>} />
        <Route path="account" element={<AccountPage />} />
        <Route
          path="admin"
          element={(
            <Guard anyOf={[PERMISSIONS.USERS_MANAGE, PERMISSIONS.AUDIT_READ, PERMISSIONS.SETTINGS_MANAGE]}>
              <AdminPage />
            </Guard>
          )}
        />
        <Route path="login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
